import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import apiUrl from "../utility/apiUrl";
import { toCollegeDetailsPath } from "../utility/collegeUrl";
import { fetchListDeduped } from "../utility/homeCollegeCache";
import INDIAN_CITIES from "../utility/indianCities";

const cityToSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// The metros that used to be the whole Location dropdown; they stay pinned at the top.
const POPULAR_LOCATIONS = [
  { value: "mumbai", label: "Mumbai" },
  { value: "delhi-ncr", label: "Delhi-NCR" },
  { value: "bengaluru", label: "Bengaluru" },
  { value: "hyderabad", label: "Hyderabad" },
  { value: "chennai", label: "Chennai" },
  { value: "kolkata", label: "Kolkata" },
  { value: "pune", label: "Pune" },
];

// Every other Indian city, A-Z, minus whatever is already pinned above (the same slug can
// only be one <option> value).
const ALL_CITY_LOCATIONS = (() => {
  const taken = new Set(["india", ...POPULAR_LOCATIONS.map((l) => l.value)]);
  const out = [];
  INDIAN_CITIES.forEach((label) => {
    const value = cityToSlug(label);
    if (!value || taken.has(value)) return;
    taken.add(value);
    out.push({ value, label });
  });
  return out;
})();

// The list page matches `city` as a substring of the college's city text, and the college data
// uses "Bangalore"/"Delhi" far more than "Bengaluru"/"Delhi-NCR" — same normalization the list
// page applies to the old slug-only links (see CITY_ALIASES in CollegeListPage.js).
const LOCATION_CITY_PARAM_OVERRIDES = {
  bengaluru: "Bangalore",
  "delhi-ncr": "Delhi",
};

const SearchCollege = ({ showFilters = false, showAutocomplete = true }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("MBA");
  // "MBA" above is only what the dropdown shows until the visitor picks something - it is not a choice they made.
  // Typing "Engineering colleges in Chennai" must not be narrowed to MBA behind their back (see handleSearch).
  const [courseTouched, setCourseTouched] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  // Category (broader academic field: Engineering & Technology, Management, Medicine &
  // Health Sciences, ...) — the list page's sidebar (CollegeFilterSidebar.js) already has
  // this filter; the homepage search didn't (user-reported 2026-09-29). "" means unset/no
  // filter, unlike Course above which always has a real value selected.
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("india");
  const [budget, setBudget] = useState("");
  const [admissionType, setAdmissionType] = useState("any");
  const [exam, setExam] = useState("any");
  const [minPlacement, setMinPlacement] = useState("");
  const [university, setUniversity] = useState("");
  const [naac, setNaac] = useState("");
  const [branch, setBranch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const navigate = useNavigate();
  const requestSeq = useRef(0);
  const autocompleteRef = useRef(null);
  const searchButtonsRef = useRef(null);

  // Search/WhatsApp buttons live at the end of the form, after the advanced fields —
  // the conventional spot. Opening "More filters" adds ~350-400px of fields below the
  // fold on a real phone/laptop screen with nothing to scroll the student there, which
  // read as the Search button "disappearing" (measured: button landed ~1070px down on
  // an 844px-tall viewport). scrollIntoView({block:"nearest"}) over-corrected — its
  // browser-native "nearest" heuristic scrolled well past what was actually needed to
  // clear the fold. Scrolling by the exact overflow (button's bottom edge past the
  // viewport bottom, plus a little breathing room) instead moves the page the minimum
  // amount required, and does nothing at all when the buttons are already visible.
  useEffect(() => {
    if (!advancedOpen) return;
    const el = searchButtonsRef.current;
    if (!el) return;
    const overflowBelow = el.getBoundingClientRect().bottom - window.innerHeight;
    if (overflowBelow > 0) {
      window.scrollBy({ top: overflowBelow + 8, behavior: "smooth" });
    }
  }, [advancedOpen]);

  // Real Course (degree-type) and Branch catalogs — same endpoints, cache keys and shape
  // as the list page's sidebar (CollegeFilterSidebar.js), so the two stay in sync
  // automatically as courses/branches are added via the admin bulk upload, instead of
  // drifting the way a second hand-picked list would.
  useEffect(() => {
    let cancelled = false;
    fetchListDeduped("courseDegreeTypes", `${apiUrl}/api/v1/college/degree-types`, {
      headers: { "ngrok-skip-browser-warning": "any" },
    }).then((names) => {
      if (cancelled || !Array.isArray(names) || names.length === 0) return;
      setCourseOptions(names.map((name) => ({ value: name, label: name })));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchListDeduped("courseBranches", `${apiUrl}/api/v1/college/branches`, {
      headers: { "ngrok-skip-browser-warning": "any" },
    }).then((names) => {
      if (cancelled || !Array.isArray(names) || names.length === 0) return;
      setBranchOptions(names.map((name) => ({ value: name, label: name })));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Same cache key as CollegeFilterSidebar.js's Category pills, so this shares its
  // fetchListDeduped result instead of firing a second request for the same data.
  useEffect(() => {
    let cancelled = false;
    fetchListDeduped("courseCategories", `${apiUrl}/api/v1/college/course-categories`, {
      headers: { "ngrok-skip-browser-warning": "any" },
    }).then((names) => {
      if (cancelled || !Array.isArray(names) || names.length === 0) return;
      setCategoryOptions(names.map((name) => ({ value: name, label: name })));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    if (showAutocomplete) setAutocompleteOpen(true);
  };

  useEffect(() => {
    if (!showAutocomplete) return;
    if (!autocompleteOpen) return;

    const onPointerDown = (e) => {
      const root = autocompleteRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setAutocompleteOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [autocompleteOpen, showAutocomplete]);

  useEffect(() => {
    if (!showAutocomplete) return;

    const term = (searchTerm || "").trim();
    if (!term || term.length < 2) {
      setSearchResults([]);
      setLoading(false);
      setAutocompleteOpen(false);
      return;
    }

    const seq = ++requestSeq.current;
    let cancelled = false;

    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          apiUrl + `/api/v1/college/search?keyword=${encodeURIComponent(term)}`,
          {
            headers: {
              "ngrok-skip-browser-warning": "any",
            },
          }
        );

        if (cancelled) return;
        if (seq !== requestSeq.current) return;
        setSearchResults(Array.isArray(response.data) ? response.data : []);
        setAutocompleteOpen(true);
      } catch (error) {
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled && seq === requestSeq.current) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchTerm, showAutocomplete]);

  // Quick picks stay on top as before; every other Indian city follows A-Z (see
  // utility/indianCities.js). `locations` is the flat union, used for label lookups.
  const popularLocations = useMemo(() => POPULAR_LOCATIONS, []);
  const allCityLocations = useMemo(() => ALL_CITY_LOCATIONS, []);
  const locations = useMemo(
    () => [{ value: "india", label: "All India" }, ...popularLocations, ...allCityLocations],
    [popularLocations, allCityLocations]
  );

  const admissionTypes = useMemo(
    () => [
      { value: "any", label: "Any" },
      { value: "entrance", label: "Entrance" },
      { value: "management", label: "Management" },
      { value: "direct", label: "Direct" },
    ],
    []
  );

  const exams = useMemo(
    () => [
      { value: "any", label: "Any" },
      { value: "cat", label: "CAT" },
      { value: "xat", label: "XAT" },
      { value: "mat", label: "MAT" },
      { value: "cmat", label: "CMAT" },
      { value: "snap", label: "SNAP" },
      { value: "gmat", label: "GMAT" },
      { value: "gre", label: "GRE" },
      { value: "jee", label: "JEE" },
      { value: "neet", label: "NEET" },
    ],
    []
  );

  // placement_rate is free text on the real data ("92%", "85% -90%", ...) — the backend
  // pulls the first number it finds and compares against this floor (see
  // CollegeInformationRepository#discoverColleges), so "80%+" still matches "85% -90%".
  const placementOptions = useMemo(
    () => [
      { value: "", label: "Any placement" },
      { value: "60", label: "60% and above" },
      { value: "70", label: "70% and above" },
      { value: "80", label: "80% and above" },
      { value: "90", label: "90% and above" },
    ],
    []
  );

  // NAAC grades actually present on file today — same small real set used by the list
  // page's sidebar (CollegeFilterSidebar.js), kept in sync deliberately: most colleges
  // have no grade on record at all ("N/A"), so this isn't every grade NAAC issues.
  const naacOptions = useMemo(
    () => [
      { value: "", label: "Any accreditation" },
      { value: "A++", label: "NAAC A++" },
      { value: "A+", label: "NAAC A+" },
      { value: "A", label: "NAAC A" },
      { value: "B++", label: "NAAC B++" },
    ],
    []
  );


  const slugify = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const selectedCourseLabel = useMemo(() => {
    const match = courseOptions.find((c) => c.value === selectedCourse);
    return match?.label || selectedCourse || "";
  }, [courseOptions, selectedCourse]);

  const selectedCategoryLabel = useMemo(() => {
    const match = categoryOptions.find((c) => c.value === selectedCategory);
    return match?.label || selectedCategory || "";
  }, [categoryOptions, selectedCategory]);

  const selectedLocationLabel = useMemo(() => {
    const match = locations.find((l) => l.value === selectedLocation);
    return match?.label || "";
  }, [locations, selectedLocation]);

  const selectedAdmissionTypeLabel = useMemo(() => {
    const match = admissionTypes.find((a) => a.value === admissionType);
    return match?.label || "";
  }, [admissionTypes, admissionType]);

  const selectedExamLabel = useMemo(() => {
    const match = exams.find((e) => e.value === exam);
    return match?.label || "";
  }, [exams, exam]);

  const filteredResults = useMemo(() => {
    const list = Array.isArray(searchResults) ? searchResults : [];

    // The plain header/mobile search box (showFilters=false) never shows the
    // Course/Location dropdowns, but selectedCourse still defaults to "MBA"
    // internally — filtering by it here would silently hide every non-MBA
    // college (the majority of results) from a search box that gives the
    // user no indication such a filter exists. Only the advanced search
    // (showFilters=true, where those dropdowns are visible and adjustable)
    // should narrow results by them.
    if (!showFilters) return list;

    // Course no longer narrows this preview: it's now the real Course (degree-type)
    // catalog, not the old 3-value Engineering/Medical/MBA vocabulary the lightweight
    // /college/search autocomplete response's collegeCategory field actually uses, so a
    // substring match against it would silently empty the preview for most real courses
    // (e.g. "Pharmacy") even when matching colleges exist. Real filtering by course still
    // happens once the student lands on the results page.
    const normalizedLocation = String(selectedLocation || "india");
    const locationSlug = normalizedLocation === "india" ? null : normalizedLocation;

    return list.filter((college) => {
      if (locationSlug) {
        const citySlug = slugify(college?.city);
        const stateSlug = slugify(college?.state);
        if (citySlug !== locationSlug && stateSlug !== locationSlug) {
          // Fallback: allow "delhi-ncr" to match "delhi" etc.
          const raw = `${citySlug} ${stateSlug}`.trim();
          if (!raw.includes(locationSlug.replace(/-ncr$/, ""))) return false;
        }
      }

      return true;
    });
  }, [searchResults, showFilters, selectedLocation]);

  const toListMenuName = (course, locationSlug) => {
    const safeCourse = slugify(course) || "mba";
    const safeLocation = (locationSlug || "india").toLowerCase();
    return `${safeCourse}-colleges-in-${safeLocation}`;
  };
 
  // Builds the same query string CollegeListPage reads on load (see its
  // "seed filters from the route" effect) so course/location/budget/placement/university/
  // accreditation/branch/keyword picked here all actually narrow the results shown there,
  // instead of only ever being relayed to a human over WhatsApp. Admission Type/Exam/Hostel
  // aren't included — see CollegeInformationRepository#discoverColleges for why (no
  // structured data behind those yet, so there's nothing honest to filter on).
  // The Course dropdown narrows the search when the visitor picked a course, or typed nothing (then the dropdowns ARE the
  // search). With words typed and the dropdown never touched, the words decide: "Engineering colleges in Chennai" is
  // read by the list page as Engineering + Chennai, and a leftover default MBA would contradict it.
  const usesCourse = (term) => courseTouched || !term;

  const buildDiscoverQuery = (term) => {
    const params = new URLSearchParams();
    if (term) params.set("keyword", term);
    // Unlike branch (which has an explicit "Any branch" empty state), the Course select
    // always has a real value selected, so it always narrows results here — matching what
    // CollegeListPage's "seed filters from route" effect reads as degreeType.
    if (selectedCourse && usesCourse(term)) params.set("degreeType", selectedCourse);
    if (selectedCategory) params.set("category", selectedCategory);
    if (selectedLocation && selectedLocation !== "india") {
      const cityLabel =
        LOCATION_CITY_PARAM_OVERRIDES[selectedLocation] ||
        locations.find((l) => l.value === selectedLocation)?.label;
      if (cityLabel) params.set("city", cityLabel);
    }
    if (budget) params.set("maxFee", budget);
    if (minPlacement) params.set("minPlacement", minPlacement);
    if (university) params.set("university", university);
    if (naac) params.set("naac", naac);
    if (branch) params.set("branch", branch);
    return params.toString();
  };

  const handleSearch = async () => {
    const term = (searchTerm || "").trim();
    setAutocompleteOpen(false);
    const courseSlug = slugify(selectedCourse) || "mba";

    if (showFilters) {
      // The page address carries a course too (/college/<course>/top/...), and the list page turns "mba" /
      // "engineering" / "medical" in it into a category filter - so an untouched default is replaced by "all".
      const pathCourse = usesCourse(term) ? selectedCourse : "all";
      const menuName = toListMenuName(pathCourse, selectedLocation);
      const query = buildDiscoverQuery(term);
      navigate(`/college/${slugify(pathCourse) || "mba"}/top/${menuName}${query ? `?${query}` : ""}`);
      return;
    }

    if (term) {
      // Navigate to dedicated results page
      navigate(`/search?keyword=${encodeURIComponent(term)}`);

      if (!showAutocomplete) {
        setLoading(false);
        setSearchResults([]);
        return;
      }

      return;
    }

    // If no college name is typed, use course + location dropdowns.
    const menuName = toListMenuName(selectedCourse, selectedLocation);
    navigate(`/college/${courseSlug}/top/${menuName}`);
  };

  const handleWhatsappEnquiry = () => {
    const term = (searchTerm || "").trim();
    const parts = [
      "Hi BharatVidyarthi Team,",
      "I’m looking for college admission guidance.",
      term ? `College keyword: ${term}` : null,
      selectedCourseLabel ? `Course: ${selectedCourseLabel}` : null,
      selectedCategoryLabel ? `Category: ${selectedCategoryLabel}` : null,
      selectedLocationLabel ? `Location: ${selectedLocationLabel}` : null,
      budget ? `Budget: ₹${budget}` : null,
      minPlacement ? `Min Placement: ${minPlacement}%+` : null,
      university ? `University: ${university}` : null,
      naac ? `Accreditation: NAAC ${naac}` : null,
      branch ? `Branch: ${branch}` : null,
      admissionType !== "any" ? `Admission Type: ${selectedAdmissionTypeLabel}` : null,
      exam !== "any" ? `Exam: ${selectedExamLabel}` : null,
    ].filter(Boolean);

    const message = parts.join("\n");
    const whatsappUrl = `https://wa.me/919884702223?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleItemClick = (result) => {
    const collegePageUrl = toCollegeDetailsPath(result?.collegeName, result?.city, result?.id);
    window.open(collegePageUrl);
    setSearchTerm("");
    setSearchResults([]);
    setAutocompleteOpen(false);
  }

  const shouldShowDropdown = showAutocomplete && autocompleteOpen && ((searchTerm || "").trim().length >= 2);
  const dropdownHasContent = loading || filteredResults.length > 0;
  const renderDropdown = () => {
    if (!shouldShowDropdown) return null;
    if (!dropdownHasContent) return null;

    return (
      <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="px-4 py-3 text-sm text-slate-600">Loading...</div>
        ) : (
          <ul className="max-h-64 overflow-auto divide-y divide-slate-100">
            {filteredResults.map((result, index) => (
              <li
                key={result?.id || index}
                className="px-4 py-3 text-sm text-slate-900 hover:bg-slate-50 cursor-pointer"
                onClick={() => handleItemClick(result)}
              >
                {result.collegeName}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
 
  return (
    <div className="w-full">
      {showFilters ? (
        <div className="space-y-2">
          {/* Essentials only, by default — the free-text box plus Course/Location/Budget.
              Everything else (Admission Type, Exam, Placement, University, Branch,
              Accreditation) is real but used far less often, so it sits behind "More
              Filters" instead of always being on screen. On a phone the old always-expanded
              version stacked all 10 fields full-width and ran ~800px tall, burying the
              search buttons off the first screen; collapsing to 4 fields by default cuts
              that by roughly half and keeps it responsive at any width since there's just
              less to stack. */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-12">
              <label className="block text-xs font-semibold text-slate-600 mb-0.5">College or Course Name (optional)</label>
              <div className="relative" ref={autocompleteRef}>
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  placeholder="e.g. IIT, or Engineering colleges in Chennai"
                  value={searchTerm}
                  onChange={handleChange}
                  onFocus={() => setAutocompleteOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setAutocompleteOpen(false);
                      return;
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                {renderDropdown()}
              </div>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-600 mb-0.5">Course</label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setCourseTouched(true);
                }}
              >
                {courseOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-600 mb-0.5">Location</label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="india">All India</option>
                <optgroup label="Popular">
                  {popularLocations.map((loc) => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="All cities (A–Z)">
                  {allCityLocations.map((loc) => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-600 mb-0.5">Budget (₹)</label>
              <input
                inputMode="numeric"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                placeholder="e.g. 500000"
                value={budget}
                onChange={(e) => {
                  const next = String(e.target.value || "").replace(/[^0-9]/g, "");
                  setBudget(next);
                }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800"
          >
            {advancedOpen ? "Fewer filters" : "More filters"}
            {advancedOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {advancedOpen && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Category</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Any category</option>
                  {categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Admission Type</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={admissionType}
                  onChange={(e) => setAdmissionType(e.target.value)}
                >
                  {admissionTypes.map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Exam</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={exam}
                  onChange={(e) => setExam(e.target.value)}
                >
                  {exams.map((ex) => (
                    <option key={ex.value} value={ex.value}>
                      {ex.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Placement</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={minPlacement}
                  onChange={(e) => setMinPlacement(e.target.value)}
                >
                  {placementOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Accreditation</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={naac}
                  onChange={(e) => setNaac(e.target.value)}
                >
                  {naacOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-6">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">University (optional)</label>
                <input
                  type="text"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  placeholder="e.g. University of Mumbai"
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-0.5">Branch</label>
                <select
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                >
                  <option value="">Any branch</option>
                  {branchOptions.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div ref={searchButtonsRef} className="flex flex-col sm:flex-row gap-2">
            <button
              className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200"
              onClick={handleSearch}
              type="button"
            >
              Search Colleges
            </button>
            <button
              className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center rounded-xl border border-green-500 bg-white px-4 py-1.5 text-sm font-semibold text-green-600 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-200"
              onClick={handleWhatsappEnquiry}
              type="button"
            >
              Enquire on WhatsApp
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div ref={autocompleteRef} className="relative w-full">
            <input
              type="text"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-200"
              placeholder="Search by college, city or course..."
              value={searchTerm}
              onChange={handleChange}
              onFocus={() => setAutocompleteOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setAutocompleteOpen(false);
                  return;
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            {renderDropdown()}
          </div>
          <button
            className="shrink-0 inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            onClick={handleSearch}
            type="button"
          >
            Search
          </button>
        </div>
      )}
    </div>
  );
};
 
export default SearchCollege;